// src/pages/UtilityAllowanceHubPage.js
// Hub page listing housing authorities with published utility allowance
// schedules — same look/structure as TranslateHubPage. Clicking a card opens
// /utility-allowances/:org, which renders the dashboard's UtilityAllowance
// component (schedule + PDF only; no analytics or ACOP).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calculator, FileCheck } from 'lucide-react';
import Brand from '../components/Brand';
import { PRICES } from '../config/uaPayment';

// ─── Responsive hook ─────────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const T = {
  blue600: '#1d4ed8', // blue-700: reads at 4.5:1 on the blue-100 chips (WCAG 1.4.3),
  blue50: '#eff6ff',
  blue100: '#dbeafe',
  gray900: '#111827',
  gray800: '#1f2937',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// Add new orgs here as their schedules are published (displayed A→Z regardless
// of order). The labeled sample link below demos Salem's schedule.
const HOUSING_AUTHORITIES = [
  // Free-trial first-cycle authorities (existing chatbot/translation
  // customers) and, later, paying customers. One line per org; the card
  // needs /public/<slug>-logo.<ext> and a published schedule in the DB.
  // `pending`: schedule not yet viewable — card renders unclickable with
  // this label in place of the arrow. Delete the property to go live.
  { slug: 'andover', name: 'Andover', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'beverly', name: 'Beverly', state: 'MA', ext: 'jpg', pending: 'Coming Jan 1, 2027' },
  { slug: 'lexington', name: 'Lexington', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'new-bedford', name: 'New Bedford', state: 'MA', pending: 'Coming Apr 1, 2027' },
  { slug: 'brockton', name: 'Brockton', state: 'MA', pending: 'Coming Nov 1, 2026' },
  { slug: 'watertown', name: 'Watertown', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'methuen', name: 'Methuen', state: 'MA', ext: 'jpg', pending: 'Coming Jan 1, 2027' },
  { slug: 'melrose', name: 'Melrose', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'haverhill', name: 'Haverhill', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'stockbridge', name: 'Stockbridge', state: 'MA', pending: 'Coming Apr 1, 2027' },
  { slug: 'tewksbury', name: 'Tewksbury', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'revere', name: 'Revere', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'framingham', name: 'Framingham', state: 'MA', pending: 'Coming Dec 1, 2026' },
  { slug: 'holliston', name: 'Holliston', state: 'MA', pending: 'Coming Dec 1, 2026' },
  { slug: 'concord', name: 'Concord', state: 'MA', pending: 'Coming Nov 1, 2026' },
  { slug: 'hudson', name: 'Hudson', state: 'MA', pending: 'Coming Jan 1, 2027' },
  // Not a "Housing Authority": the city's Community Development Authority is
  // the PHA (MA070) and runs Section 8 through its housing division.
  { slug: 'marlborough', name: 'Marlborough', state: 'MA', fullName: 'Marlborough Community Development Authority', pending: 'Coming Jan 1, 2027' },
  { slug: 'arlington', name: 'Arlington', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'waltham', name: 'Waltham', state: 'MA', pending: 'Coming Nov 1, 2026' },
  { slug: 'somerville', name: 'Somerville', state: 'MA', ext: 'jpg', pending: 'Coming Jan 1, 2027' },
  { slug: 'winchester', name: 'Winchester', state: 'MA', pending: 'Coming Dec 1, 2026' },
  { slug: 'salem', name: 'Salem', state: 'MA', pending: 'Coming Nov 1, 2026' },
  { slug: 'pittsfield', name: 'Pittsfield', state: 'MA', ext: 'webp', pending: 'Coming Apr 1, 2027' },
  { slug: 'wakefield', name: 'Wakefield', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'brookline', name: 'Brookline', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'woburn', name: 'Woburn', state: 'MA', pending: 'Coming Apr 1, 2027' },
  // effective 2027-05-01 (the authority's own May 1 annual cycle)
  { slug: 'fall-river', name: 'Fall River', state: 'MA', pending: 'Coming May 1, 2027' },
  // effective 2027-05-01 (their own May 1 cycle). Three schedules, one per
  // utility territory: Taunton/E Taunton/Berkley/Raynham (TMLP electric),
  // Dighton/Norton/Rehoboth, Wareham/Onset/Buzzards Bay. One card for now.
  { slug: 'taunton', name: 'Taunton', state: 'MA', pending: 'Coming May 1, 2027' },
  // Managed by Quincy Housing Authority, which runs five authorities; the
  // fifth, Marshfield, administers state MRVP vouchers rather than federal
  // Section 8 and so is deliberately not listed here.
  { slug: 'quincy', name: 'Quincy', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'hingham', name: 'Hingham', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'holbrook', name: 'Holbrook', state: 'MA', pending: 'Coming Jan 1, 2027' },
  { slug: 'hanson', name: 'Hanson', state: 'MA', pending: 'Coming Jan 1, 2027' },
  // effective 2027-01-01
  { slug: 'reading', name: 'Reading', state: 'MA', pending: 'Coming Jan 1, 2027' },
  // effective 2027-01-01
  { slug: 'north-reading', name: 'North Reading', state: 'MA', ext: 'jpg', pending: 'Coming Jan 1, 2027' },
  // effective 2027-01-01
  { slug: 'milford', name: 'Milford', state: 'MA', pending: 'Coming Jan 1, 2027' },
  // effective 2027-01-01 (card added 2026-10-26; the authority's own
  // current set is dated 12/01/2026 on greenfieldhousing.org)
  { slug: 'greenfield', name: 'Greenfield', state: 'MA', ext: 'jpg', pending: 'Coming Jan 1, 2027' },
  // effective 2027-01-01. The authority has no logo of its own, so the card
  // uses the Town of Danvers seal.
  { slug: 'danvers', name: 'Danvers', state: 'MA', ext: 'webp', pending: 'Coming Jan 1, 2027' },
  // Regional administering agency, not a local housing authority: EOHLC
  // publishes regional schedules for the MTW side, the RAA's own non-MTW
  // vouchers are its own responsibility. `kind: 'raa'` groups it above the
  // authorities and keeps it out of the 118 denominator.
  { slug: 'neighborworks', name: 'NeighborWorks', state: 'MA', kind: 'raa', fullName: 'NeighborWorks Housing Solutions', pending: 'Coming Dec 1, 2026' },
].sort((a, b) => a.name.localeCompare(b.name));

// Regional administering agencies list above local housing authorities; the
// array above is already A->Z, so each group stays alphabetical within itself.
const GROUP_ORDER = [
  { kind: 'raa', label: 'Regional Administering Agencies' },
  { kind: 'ha', label: 'Housing Authorities' },
];

function groupOrgs(list) {
  return GROUP_ORDER
    .map(g => ({ ...g, items: list.filter(o => (o.kind || 'ha') === g.kind) }))
    .filter(g => g.items.length > 0);
}

// The 118 denominator counts local housing authorities only; RAAs are a
// separate tier and are not part of it.
const HA_COUNT = HOUSING_AUTHORITIES.filter(o => (o.kind || 'ha') === 'ha').length;


export default function UtilityAllowanceHubPage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [search, setSearch] = useState('');

  const q = search.toLowerCase().trim();
  const visible = HOUSING_AUTHORITIES.filter(ha =>
    !q || `${ha.name} ${ha.fullName || ''} ${ha.kind === 'raa' ? 'regional administering agency' : 'housing authority'} ${ha.state} ${ha.slug}`.toLowerCase().includes(q)
  );

  const cardHover = (e, hovering) => {
    e.currentTarget.style.borderColor = hovering ? T.blue600 : T.gray200;
    e.currentTarget.style.transform = hovering ? 'translateY(-2px)' : 'translateY(0)';
    e.currentTarget.style.boxShadow = hovering ? T.shadow : T.shadowSm;
  };

  return (
    <div style={{
      fontFamily: T.font,
      minHeight: '100vh',
      background: T.gradient,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.gray200}`,
        padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: mobile ? 'flex-start' : 'center',
          gap: mobile ? '0.5rem' : '0.75rem',
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
        }}>
          {/* Back button + logo row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            width: mobile ? '100%' : 'auto',
          }}>
            <button
              onClick={() => navigate('/')}
              style={{
                ...(mobile ? {} : { position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }),
                background: T.gray100,
                border: `1px solid ${T.gray200}`,
                borderRadius: T.radiusPill,
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: T.gray600,
                padding: '0.45rem 0.85rem',
                transition: 'all 0.15s ease',
                fontFamily: T.font,
                fontWeight: 600,
                flexShrink: 0,
              }}
              title="Back to Home"
              onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.borderColor = T.blue600; e.currentTarget.style.color = T.blue600; }}
              onMouseOut={e => { e.currentTarget.style.background = T.gray100; e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.color = T.gray600; }}
            >
              ←
            </button>
            {/* Logo + badge */}
            <Brand as="a" href="https://gethousingai.com" target="_blank" rel="noopener noreferrer" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', fontFamily: T.font, textDecoration: 'none' }} />
            <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
            <span style={{
              fontSize: mobile ? '0.72rem' : '0.85rem',
              padding: mobile ? '0.2rem 0.55rem' : '0.25rem 0.75rem',
              background: T.blue100,
              color: T.blue600,
              borderRadius: T.radiusPill,
              fontWeight: 600,
            }}>
              Utility Allowance Schedules
            </span>
          </div>
        </div>
      </header>

      {/* Main */}
      <main style={{ padding: mobile ? '1.25rem 0.75rem' : '2.5rem 1.5rem', flex: 1 }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          {/* Hero */}
          <div style={{
            textAlign: 'center',
            marginBottom: mobile ? '1.5rem' : '2.5rem',
            padding: mobile ? '1.5rem 1rem' : '2.5rem 2rem',
            background: T.gradient,
            borderRadius: T.radius,
            boxShadow: T.shadow,
          }}>
            <h1 style={{
              fontSize: mobile ? '1.35rem' : '2rem',
              fontWeight: 800,
              color: T.gray900,
              fontFamily: T.font,
              marginBottom: '0.75rem',
              letterSpacing: '-0.02em',
            }}>
              The Massachusetts Utility Allowance Registry
            </h1>
            <p style={{
              color: T.gray600,
              fontSize: mobile ? '0.84rem' : '0.92rem',
              maxWidth: '560px',
              margin: '0 auto',
              lineHeight: 1.65,
              marginBottom: '1rem',
              textAlign: 'justify',
              textAlignLast: 'center',
            }}>
              This is the statewide registry of Section 8 utility allowance schedules:
              one public home for every Massachusetts housing authority and regional
              administering agency. Each is computed the same way, with HUD&rsquo;s Utility
              Schedule Model (HUSM) and current, cited rates, all regenerated together each
              January&nbsp;1st on one synced cycle. Every figure traces to its source filing.
            </p>

            {/* Statewide goal progress */}
            <div style={{ maxWidth: '560px', margin: '0 auto 1.25rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '0.45rem', gap: '0.3rem', textAlign: 'center' }}>
                <span style={{ fontSize: mobile ? '0.78rem' : '0.84rem', fontWeight: 700, color: T.gray900 }}>
                  {HA_COUNT} of the 118 MA housing authorities that administer Section 8 are on the registry
                </span>
              </div>
              <div style={{ height: '10px', background: T.gray100, borderRadius: T.radiusPill, overflow: 'hidden', border: `1px solid ${T.gray200}` }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(100, Math.round((HA_COUNT / 118) * 100))}%`,
                  minWidth: '2.5rem',
                  background: 'linear-gradient(90deg, #2563eb, #7c3aed)',
                  borderRadius: T.radiusPill,
                  transition: 'width 0.6s ease',
                }} />
              </div>
            </div>
            <p style={{
              color: T.gray500,
              fontSize: mobile ? '0.74rem' : '0.78rem',
              maxWidth: '560px',
              margin: '0 auto',
              lineHeight: 1.5,
              fontStyle: 'italic',
              padding: mobile ? '0.6rem 0.75rem' : '0.75rem 1rem',
              background: 'rgba(245,158,11,0.08)',
              borderRadius: T.radiusSm,
              border: '1px solid rgba(245,158,11,0.15)',
            }}>
              HousingAI computes these schedules for annual review under 24 CFR
              &sect; 982.517. The authority&rsquo;s adopted schedule governs; confirm
              with the authority before relying on them.
            </p>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: mobile ? '0.75rem' : '1.1rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/utility-allowances/join')}
                style={{
                  background: T.blue600,
                  border: 'none',
                  borderRadius: T.radiusPill,
                  cursor: 'pointer',
                  fontFamily: T.font,
                  fontSize: mobile ? '0.84rem' : '0.9rem',
                  fontWeight: 700,
                  color: T.white,
                  padding: mobile ? '0.5rem 1.1rem' : '0.6rem 1.4rem',
                  boxShadow: T.shadowSm,
                  transition: 'background 0.15s ease',
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; }}
                onMouseOut={e => { e.currentTarget.style.background = T.blue600; }}
              >
                Get your schedule
              </button>
              <button
                onClick={() => navigate('/utility-allowances/salem/sample')}
                style={{
                  background: T.white,
                  border: `1.5px solid ${T.blue600}`,
                  borderRadius: T.radiusPill,
                  cursor: 'pointer',
                  fontFamily: T.font,
                  fontSize: mobile ? '0.84rem' : '0.9rem',
                  fontWeight: 700,
                  color: T.blue600,
                  padding: mobile ? '0.5rem 1.1rem' : '0.6rem 1.4rem',
                  transition: 'background 0.15s ease',
                }}
                onMouseOver={e => { e.currentTarget.style.background = T.blue50; }}
                onMouseOut={e => { e.currentTarget.style.background = T.white; }}
              >
                View a sample schedule (Salem, MA)
              </button>
            </div>

          </div>

          {/* Benefit strip */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '0.75rem',
            marginBottom: mobile ? '1.25rem' : '1.75rem',
          }}>
            {[
              { title: 'Instant portability lookups', Icon: Search, body: 'Your annual purchase includes every participating PHA’s current chart, on demand. No calls, no waiting, no PDFs of unknown vintage.' },
              { title: 'Line-by-line math shown', Icon: Calculator, body: 'Not just the final allowance amount. We include the actual calculation behind every figure, so you know the basis for every number on the chart.' },
              { title: 'The 10% test, in writing', Icon: FileCheck, body: 'At each annual renewal, a category-by-category 10% check against the prior year’s schedule, with a written basis for revising or not revising.' },
            ].map(b => (
              <div key={b.title} style={{
                background: T.white,
                border: `1px solid ${T.gray200}`,
                borderRadius: T.radiusSm,
                padding: '0.9rem 1rem',
                boxShadow: T.shadowSm,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.35rem',
                    background: T.blue50,
                    borderRadius: '8px',
                    flexShrink: 0,
                  }}>
                    <b.Icon size={16} color={T.blue600} strokeWidth={2} />
                  </span>
                  <span style={{ fontWeight: 700, color: T.gray900, fontSize: '0.85rem' }}>{b.title}</span>
                </div>
                <div style={{ color: T.gray600, fontSize: '0.78rem', lineHeight: 1.5 }}>{b.body}</div>
              </div>
            ))}
          </div>

          {/* No authorities onboard yet: skip search/list. The sticky join CTA
              at the bottom of the page carries the "Get your schedule" action. */}
          {HOUSING_AUTHORITIES.length === 0 && (
            <div style={{ textAlign: 'center', padding: '1.5rem 0 0.5rem' }}>
              <p style={{ color: T.gray600, fontSize: '0.95rem', margin: 0, lineHeight: 1.6 }}>
                Participating housing authorities&rsquo; schedules will be listed here.
              </p>
            </div>
          )}

          {/* Search */}
          {HOUSING_AUTHORITIES.length > 0 && (
          <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search housing authorities…"
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.75rem',
                fontSize: '0.95rem',
                border: `1px solid ${T.gray200}`,
                borderRadius: T.radiusPill,
                outline: 'none',
                fontFamily: T.font,
                color: T.gray900,
                background: T.white,
                boxShadow: T.shadowSm,
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              }}
              onFocus={e => { e.target.style.borderColor = T.blue600; e.target.style.boxShadow = `0 0 0 3px ${T.blue50}`; }}
              onBlur={e => { e.target.style.borderColor = T.gray200; e.target.style.boxShadow = T.shadowSm; }}
            />
            <svg style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.gray400} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                  background: T.gray100, border: 'none', borderRadius: '50%',
                  width: '22px', height: '22px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', color: T.gray500, fontWeight: 700,
                }}
              >×</button>
            )}
          </div>
          )}

          {/* No results */}
          {q && visible.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <p style={{ color: T.gray500, fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                No housing authorities found. Yours isn&rsquo;t on board yet.
              </p>
              <button
                onClick={() => navigate('/utility-allowances/join')}
                style={{
                  padding: '0.6rem 1.4rem',
                  background: T.blue600,
                  color: T.white,
                  border: 'none',
                  borderRadius: T.radiusPill,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  fontFamily: T.font,
                }}
              >
                Get your schedule →
              </button>
            </div>
          )}

          {/* Org Cards */}
          {visible.length > 0 && (
            <p style={{
              fontSize: mobile ? '0.85rem' : '0.9rem',
              fontWeight: 600,
              color: T.gray800,
              marginBottom: '0.75rem',
            }}>
              Select an agency below to view its schedule:
            </p>
          )}
          {groupOrgs(visible).map(group => (
            <div key={group.kind} style={{ marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '0 0 0.6rem' }}>
                <span style={{ fontSize: mobile ? '0.8rem' : '0.85rem', fontWeight: 800, color: T.gray900, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {group.label}
                </span>
                <span style={{ flex: 1, height: '1px', background: T.gray200 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {group.items.map(ha => (
              <button
                key={ha.slug}
                onClick={() => { if (!ha.pending) navigate(`/utility-allowances/${ha.slug}`); }}
                disabled={!!ha.pending}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: mobile ? '0.75rem' : '1rem',
                  padding: mobile ? '0.85rem 1rem' : '1.25rem 1.5rem',
                  background: T.white,
                  border: `1px solid ${T.gray200}`,
                  borderRadius: T.radiusSm,
                  cursor: ha.pending ? 'default' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  fontFamily: T.font,
                  boxShadow: T.shadowSm,
                }}
                onMouseOver={e => { if (!ha.pending) cardHover(e, true); }}
                onMouseOut={e => { if (!ha.pending) cardHover(e, false); }}
              >
                <img
                  src={`/${ha.slug}-logo.${ha.ext || 'png'}`}
                  alt=""
                  style={{ height: mobile ? '32px' : '40px', width: mobile ? '32px' : '40px', objectFit: 'contain', borderRadius: '6px', flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '0.92rem' : '1.05rem' }}>
                    {ha.fullName || `${ha.name} Housing Authority`}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: T.gray600, marginTop: '0.15rem' }}>
                    {ha.state}
                  </div>
                </div>
                {ha.pending ? (
                  <span style={{
                    fontSize: mobile ? '0.72rem' : '0.8rem',
                    fontWeight: 600,
                    color: T.blue600,
                    background: T.blue50,
                    border: `1px solid ${T.blue100}`,
                    borderRadius: T.radiusPill,
                    padding: '0.3rem 0.75rem',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}>
                    {ha.pending}
                  </span>
                ) : (
                  <span style={{ fontSize: '1.1rem', color: T.blue600, fontWeight: 600, flexShrink: 0 }}>→</span>
                )}
              </button>
                ))}
              </div>
            </div>
          ))}

          {/* Join CTA — sticky so it stays on screen no matter how long the list gets */}
          <div style={{
            position: 'sticky',
            bottom: '0.75rem',
            marginTop: mobile ? '1.5rem' : '2rem',
            padding: mobile ? '0.7rem 0.9rem' : '0.8rem 1.25rem',
            background: T.white,
            border: `1px solid ${T.blue600}`,
            borderRadius: T.radiusSm,
            boxShadow: T.shadow,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: mobile ? '0.5rem' : '0.75rem',
            flexWrap: 'wrap',
            textAlign: 'center',
            zIndex: 20,
          }}>
            <span style={{ fontSize: mobile ? '0.8rem' : '0.9rem', color: T.gray800 }}>
              Don&rsquo;t see your agency? Help us get every authority synced in one place.
              {' '}
              <span style={{ color: T.gray500, fontWeight: 600 }}>
                ${PRICES.standard}/yr &middot; ${PRICES.member}/yr for Section 8 Administrators members
              </span>
            </span>
            <button
              onClick={() => navigate('/utility-allowances/join')}
              style={{
                padding: mobile ? '0.4rem 0.9rem' : '0.45rem 1.1rem',
                background: T.blue600,
                color: T.white,
                border: 'none',
                borderRadius: T.radiusPill,
                cursor: 'pointer',
                fontFamily: T.font,
                fontSize: mobile ? '0.8rem' : '0.88rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Get your schedule →
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ background: T.gray900, color: T.white, padding: mobile ? '1.25rem 0' : '2rem 0', fontFamily: T.font }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto', padding: '0 1rem',
          display: 'flex', flexDirection: mobile ? 'column' : 'row',
          flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
          gap: mobile ? '0.5rem' : '1rem',
        }}>
          <Brand theme="dark" markClassName="text-blue-600"
            style={{ fontWeight: 600, fontSize: '0.95rem' }} />
          <div style={{ fontSize: mobile ? '0.78rem' : '0.875rem', color: '#d1d5db' }}>
            © 2026 HousingAI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
